import React, { useEffect, useState } from "react";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";

import { Box, Typography } from "@mui/material";
import EasyAnchorLink from "../../UI/AnchorLink";

import { GridColDef } from "@mui/x-data-grid-premium";
import { renderNumberCell } from "utils/dataGrid";
import GaneshDataGrid from "../../DataGrid/GaneshDataGrid";

import "../BrandMetadataReview.css";
import PlacesDrillDownModal from "../UI/PlacesDrillDownModal";

interface PlacesOmniReviewProps {
  omni_key: string;
  country_code: string;
  chain_id: string;
}

const ReviewPlacesOmniLocationNames: React.FC<PlacesOmniReviewProps> = ({
  omni_key,
  country_code,
  chain_id,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>([]);

  const { showError } = useError();
  const { apiRequest } = useApi();

  const columns_def: GridColDef[] = [
    {
      field: "chain_id",
      headerName: "Chain ID",
      description: "The chain ID.",
      type: "string",
      width: 200,
    },
    {
      field: "location_name",
      headerName: "Location Name",
      description:
        "The name of the location as found in the brand_place_omni_association table.",
      type: "string",
      width: 250,
    },
    {
      field: "location_count",
      headerName: "Location Count",
      description:
        "The count of occurrences of the location name in the associated data.",
      type: "number",
      width: 180,
      renderCell: renderNumberCell,
    },
    {
      field: "drill_down",
      headerName: "View Places",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const api = "/brand_places/get_brand_places_by_omni_locname";
        const apiParams = {
          chain_id: chain_id,
          omni_key: omni_key,
          country_code: country_code,
          location_name: params.row.location_name,
        };

        return (
          <>
            <PlacesDrillDownModal api={api} apiParams={apiParams} />
          </>
        );
      },
    },
  ];

  useEffect(() => {
    if (omni_key && country_code) {
      setLoading(true);
      const params = new URLSearchParams({
        omni_key: omni_key,
        country_code: country_code,
      });
      const fetchOmniLocationNames = async () => {
        try {
          const response = await apiRequest<any>(
            `/brand_metadata/location_name_review?${params.toString()}`,
            {
              method: "GET",
            }
          );
          if (response.data) {
            setData(response.data);
          }
        } catch (error) {
          console.error(error);
          showError(
            `Failed to fetch places location names to review for omni ${omni_key} + ${country_code}.`
          );
        } finally {
          setLoading(false);
        }
      };
      fetchOmniLocationNames();
    } else {
      setData([]);
    }
  }, [omni_key, country_code]);

  return (
    <Box className="metadata-review-container">
      <Box display="flex" alignItems="center" id="location-names">
        <EasyAnchorLink id="location-names" />
        <Typography variant="h6" gutterBottom sx={{ fontSize: "1.25rem" }}>
          Location Names
        </Typography>
      </Box>

      <GaneshDataGrid
        gridId="placesReviewOmniLocationNames"
        columns={columns_def}
        data={data}
        loading={loading}
        headerFilters={data.length > 0 ? true : false}
        customHeight={data.length > 0 ? "750px" : "300px"}
      />
    </Box>
  );
};

export default ReviewPlacesOmniLocationNames;
